import i18next from 'i18next';
import { call, put, takeLatest } from 'redux-saga/effects';

import { HTTP_STATUS } from '@talend/http';
import { pushNotification, NotificationType } from '@talend/notification-center';

import {
	patchCustomAttributesDefinition,
	getCustomAttributeDefinition,
	postCustomAttributesDefinition,
} from '../../api/customAttributes';
import {
	goToCADListAction,
	setCADFormDataAction,
	setCADFormBeginLoadingAction,
	setCADFormStopLoadingAction,
} from './CADForm.actions';
import {
	CREATE_CUSTOM_ATTRIBUTES_DEFINITION,
	GET_CUSTOM_ATTRIBUTE_DEFINITION_FORM_DATA,
	UPDATE_CUSTOM_ATTRIBUTES_DEFINITION,
} from './CADForm.constants';

function* sendCustomAttributeDefinitionUpdateSuccessful(label: string): Generator {
	yield put(
		pushNotification(
			null,
			i18next.t('tdc-app:UPDATE_CUSTOM_ATTRIBUTE_DEFINITION_SUCCESSFUL', {
				defaultValue: 'The custom attribute definition {{customAttrDef}} was updated successfully.',
				customAttrDef: label,
			}),
			NotificationType.info,
		),
	);
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function* getCADFormData({ payload: { id } }: any): Generator {
	yield put(setCADFormBeginLoadingAction);
	const { data, response }: any = yield call(getCustomAttributeDefinition, id);
	yield put(setCADFormStopLoadingAction);
	if (response.ok) {
		yield put(setCADFormDataAction(data));
	}
	return yield response;
}

// <string | React.ReactElement<any, string | React.JSXElementConstructor<any>>
//  string | string[] | React.ReactElement<any, string | React.JSXElementConstructor<any>> | React.ReactElement<any, string | React.JSXElementConstructor<any>>[]

function* sendCADDefinitionCreationSuccessful(label: string): Generator {
	yield put(
		pushNotification(
			null,
			i18next.t('tdc-app:CREATE_CUSTOM_ATTRIBUTE_DEFINITION_SUCCESSFUL', {
				defaultValue: 'The custom attribute definition {{customAttrDef}} was created successfully.',
				customAttrDef: label,
			}),
			NotificationType.info,
		),
	);
}

function* goToCADList() {
	yield put(goToCADListAction);
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function* createCustomAttributesDefinition({ payload: { value } }: any): Generator {
	yield put(setCADFormBeginLoadingAction);
	try {
		yield call(postCustomAttributesDefinition, value);
		yield put(setCADFormStopLoadingAction);
		if (window.location.toString().includes('/custom-attributes/add')) {
			yield goToCADList();
		}
		return yield sendCADDefinitionCreationSuccessful(value.label);
	} catch (e) {
		const error = e as unknown as any;
		const notificationMessage =
			error.data?.status === HTTP_STATUS.BAD_REQUEST
				? error.data.message
				: i18next.t(
						'tdc-app:CREATE_CUSTOM_ATTRIBUTE_DEFAULT_ERROR_MESSAGE',
						'Something went wrong',
					);
		yield put(
			pushNotification(
				i18next.t('tdc-app:CREATE_CUSTOM_ATTRIBUTE_DEFAULT_ERROR_TITLE', 'Error'),
				notificationMessage,
				NotificationType.error,
			),
		);
		return yield put(setCADFormStopLoadingAction);
	}
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function* updateCustomAttributesDefinition({ payload: { id, value } }: any): Generator {
	yield put(setCADFormBeginLoadingAction);
	const ret: any = yield call(patchCustomAttributesDefinition, id, value);
	yield put(setCADFormStopLoadingAction);
	if (!ret.response.ok) {
		return yield ret;
	}
	if (window.location.toString().includes('/custom-attributes')) {
		yield goToCADList();
	}
	return yield sendCustomAttributeDefinitionUpdateSuccessful(value.label);
}

export default function* (): Generator {
	yield takeLatest(CREATE_CUSTOM_ATTRIBUTES_DEFINITION, createCustomAttributesDefinition);
	yield takeLatest(UPDATE_CUSTOM_ATTRIBUTES_DEFINITION, updateCustomAttributesDefinition);
	yield takeLatest(GET_CUSTOM_ATTRIBUTE_DEFINITION_FORM_DATA, getCADFormData);
}
